import React from "react";
import {
    Button, CircularProgress, TextField, Typography, Box,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "hooks";
import { toast } from "react-toastify";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useBulkImportLeadsMutation } from "store/services/vendorLeadApi";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useTheme } from "@mui/material/styles";

const breadcrumbs = [
    { title: "Leads", path: "/app/crm/vendor-lead/list" },
    { title: "Bulk Import Review" },
];

const HEADERS = [
    { label: "Name", field: "name" },
    { label: "Company Name", field: "companyName" },
    { label: "Contact Person", field: "contactPersonName" },
    { label: "Phone", field: "phone" },
    { label: "Alternate Phone", field: "alternatePhone" },
    { label: "Email", field: "email" },
    { label: "Address", field: "address" },
    { label: "Industry Type", field: "industryType" },
    { label: "Current Provider", field: "currentProvider" },
    { label: "Est. Monthly Vol.", field: "estimatedMonthlyVolume" },
    { label: "Service Type", field: "preferredServiceType" },
    { label: "Pain Points", field: "painPoints" },
    { label: "Preferred Routes", field: "preferredRoutes" },
];

const hasNameField = (lead) =>
    !!(lead.name || lead.companyName || lead.contactPersonName);

const hasContactField = (lead) => !!(lead.email || lead.phone);

const getRowError = (lead) => {
    if (!hasNameField(lead)) return "Name required (Name, Company, or Contact Person)";
    if (!hasContactField(lead)) return "Email or Phone required";
    return null;
};

const displayValue = (value) => {
    if (Array.isArray(value)) return value.join(", ");
    return value ?? "";
};

const VendorLeadReviewScreen = () => {
    const { state } = useLocation();
    const { leads = [] } = state || {};
    const navigate = useNavigate();
    const { viewEntity } = useAuth();
    const theme = useTheme();

    const account = viewEntity?.entity?.account?._id ?? viewEntity?.entity?.account;
    const entity = viewEntity?.entity?._id;

    const { control, reset, handleSubmit } = useForm({
        defaultValues: { reviewData: leads },
    });

    const reviewData = useWatch({ control, name: "reviewData" });
    const [bulkImport, { isLoading: importing }] = useBulkImportLeadsMutation();

    React.useEffect(() => {
        if (leads.length) reset({ reviewData: leads });
    }, []);

    const handleImport = async (data) => {
        try {
            const payload = data.reviewData.map((lead) => ({
                ...lead,
                painPoints: typeof lead.painPoints === "string"
                    ? lead.painPoints.split(",").map((s) => s.trim()).filter(Boolean)
                    : lead.painPoints ?? [],
                preferredRoutes: typeof lead.preferredRoutes === "string"
                    ? lead.preferredRoutes.split(",").map((s) => s.trim()).filter(Boolean)
                    : lead.preferredRoutes ?? [],
                estimatedMonthlyVolume: lead.estimatedMonthlyVolume
                    ? Number(lead.estimatedMonthlyVolume) : undefined,
                account,
                entity,
            }));

            const res = await bulkImport({ leads: payload }).unwrap();

            // ── Navigate to the summary screen with full results ──
            navigate("/app/crm/vendor-lead/import-summary", {
                state: {
                    results: res.results,          // per-row { tag, status, reason }
                    leads: data.reviewData,        // original rows for name/company display
                    importedCount: res.importedCount,
                    totalCount: res.totalCount,
                },
            });

        } catch (err) {
            toast.error(err?.data?.message ?? "Bulk import failed");
        }
    };

    if (!leads.length) {
        return (
            <BreadcrumbLayout breadcrumbs={breadcrumbs}>
                <BreadcrumbLayout.Paper>
                    <Box p={3}>
                        <Typography color="text.secondary">
                            No lead data to review. Please go back and upload a file.
                        </Typography>
                    </Box>
                </BreadcrumbLayout.Paper>
            </BreadcrumbLayout>
        );
    }

    const errorCount = reviewData?.filter((r) => !!getRowError(r)).length ?? 0;

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} isBusy={importing}>
            <BreadcrumbLayout.Paper>

                {/* ── Summary ── */}
                <Box px={3} pt={3} pb={1} display="flex" alignItems="center" gap={2}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        {reviewData?.length ?? 0} rows to import
                    </Typography>
                    {errorCount > 0 && (
                        <Typography variant="caption" color="error.main">
                            {errorCount} row(s) have errors and will be skipped
                        </Typography>
                    )}
                </Box>

                {/* ── Table ── */}
                <Box px={3} pb={3}>
                    <Box
                        sx={{
                            overflowX: "auto",
                            overflowY: "auto",
                            maxHeight: 520,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                        }}
                    >
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1600 }}>
                            <thead>
                                <tr>
                                    <th style={thStyle(theme)}>#</th>
                                    {HEADERS.map((h) => (
                                        <th key={h.field} style={thStyle(theme)}>{h.label}</th>
                                    ))}
                                    <th style={thStyle(theme)}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reviewData?.map((row, rowIdx) => {
                                    const rowError = getRowError(row);
                                    return (
                                        <tr key={rowIdx} style={trStyle(theme, rowIdx, !!rowError)}>
                                            <td style={tdStyle(theme)}>{rowIdx + 1}</td>
                                            {HEADERS.map(({ field }) => (
                                                <td key={field} style={tdStyle(theme)}>
                                                    <Controller
                                                        name={`reviewData[${rowIdx}].${field}`}
                                                        control={control}
                                                        defaultValue={displayValue(row[field])}
                                                        render={({ field: f }) => (
                                                            <TextField
                                                                {...f}
                                                                size="small"
                                                                variant="outlined"
                                                                fullWidth
                                                                inputProps={{ style: { fontSize: 12 } }}
                                                                sx={{ minWidth: 120 }}
                                                            />
                                                        )}
                                                    />
                                                </td>
                                            ))}
                                            <td style={tdStyle(theme)}>
                                                {rowError ? (
                                                    <Typography variant="caption" color="error.main">
                                                        {rowError}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="caption" color="success.main">
                                                        Ready
                                                    </Typography>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Box>
                </Box>

                {/* ── Actions ── */}
                <Box px={3} pb={3} display="flex" justifyContent="flex-end" gap={2}>
                    {importing && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">Importing...</Typography>
                        </Box>
                    )}
                    <Button
                        variant="outlined"
                        disabled={importing}
                        onClick={() => navigate("/app/crm/vendor-lead/list")}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={importing}
                        onClick={handleSubmit(handleImport)}
                    >
                        Finalize Import
                    </Button>
                </Box>

            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default VendorLeadReviewScreen;

// ── Theme-aware styles ────────────────────────────────────────────────────────
const thStyle = (theme) => ({
    padding: "10px 12px",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    fontSize: 12,
    textAlign: "left",
    whiteSpace: "nowrap",
    borderRight: `1px solid ${theme.palette.divider}`,
    position: "sticky",
    top: 0,
    zIndex: 1,
});

const trStyle = (theme, rowIdx, hasError) => ({
    backgroundColor: hasError
        ? theme.palette.mode === "dark"
            ? "rgba(211,47,47,0.15)"
            : "rgba(211,47,47,0.06)"
        : rowIdx % 2 === 0
            ? theme.palette.action.hover
            : theme.palette.background.paper,
});

const tdStyle = (theme) => ({
    padding: "6px 8px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    verticalAlign: "middle",
});